/**
 * OAuth-id-token capture for the account-binding flow.
 *
 * We deliberately do not reuse `/api/auth/callback/[provider]` here — that
 * route is the LOGIN path and mints a fresh auth cookie. For binding we want
 * to keep the current session, decode the id_token returned by the provider
 * SDK, and post `{email, social_id}` to our binding proxy.
 *
 * The provider SDKs (Google GSI / Sign in with Apple JS) already handle the
 * popup + signed-id-token roundtrip with their respective auth servers, so
 * the values we extract here have been issued by Google / Apple within the
 * browser session. Defence-in-depth verification belongs in the binding
 * proxy, not here.
 */

export const GOOGLE_OAUTH_CLIENT_ID =
  "281626020447-k6cm18mjalauo05jsg0v2orsmdb8r0c9.apps.googleusercontent.com"
export const APPLE_OAUTH_CLIENT_ID = "com.yohomobile.esim.web"

export type OAuthIdentity = {
  email: string
  socialId: string
}

function decodeJwtPayload(token: string): Record<string, unknown> | null {
  const parts = token.split(".")
  if (parts.length < 2) return null
  try {
    const payload = parts[1].replace(/-/g, "+").replace(/_/g, "/")
    const normalized = payload + "=".repeat((4 - (payload.length % 4)) % 4)
    return JSON.parse(atob(normalized))
  } catch {
    return null
  }
}

function identityFromIdToken(idToken: string): OAuthIdentity | null {
  const payload = decodeJwtPayload(idToken)
  if (!payload) return null
  const email = typeof payload.email === "string" ? payload.email : ""
  const sub = typeof payload.sub === "string" ? payload.sub : ""
  if (!sub) return null
  return { email, socialId: sub }
}

export class OAuthCancelledError extends Error {
  constructor(public code: string = "user_cancelled") {
    super(code)
    this.name = "OAuthCancelledError"
  }
}

// Codes that mean "the user cancelled / dismissed the popup" rather than a
// real failure. We treat these as silent no-ops in the UI.
const GOOGLE_CANCEL_CODES = new Set([
  "tap_outside",
  "user_cancel",
  "user_cancelled",
  "dismissed",
  "popup_closed",
  "popup_closed_by_user",
  "access_denied",
])

const APPLE_CANCEL_CODES = new Set([
  "popup_closed_by_user",
  "user_cancelled_authorize",
  "user_canceled_authorize",
  "user_cancelled",
])

export function isOAuthCancellation(error: unknown) {
  if (!error) return false
  const code =
    error instanceof Error
      ? error.message
      : typeof error === "string"
        ? error
        : ""
  return (
    GOOGLE_CANCEL_CODES.has(code) ||
    APPLE_CANCEL_CODES.has(code) ||
    error instanceof OAuthCancelledError
  )
}

export function captureGoogleIdentity(): Promise<OAuthIdentity> {
  return new Promise<OAuthIdentity>((resolve, reject) => {
    if (typeof window === "undefined") {
      reject(new Error("oauth_browser_only"))
      return
    }
    const google = (window as any).google
    // We deliberately switch to `accounts.oauth2.initTokenClient` here instead
    // of the `accounts.id` One Tap prompt. One Tap is a passive suggestion
    // UI that users routinely tap-outside-to-dismiss (returning the
    // `tap_outside` skip reason) — not what someone clicking a "Link Google"
    // button expects. The token client opens an explicit OAuth popup and is
    // robust against the FedCM migration noise we were also seeing.
    if (!google?.accounts?.oauth2?.initTokenClient) {
      reject(new Error("google_sdk_not_ready"))
      return
    }

    let settled = false
    const settle = (fn: () => void) => {
      if (settled) return
      settled = true
      fn()
    }

    const tokenClient = google.accounts.oauth2.initTokenClient({
      client_id: GOOGLE_OAUTH_CLIENT_ID,
      scope: "openid email profile",
      callback: async (response: {
        access_token?: string
        error?: string
      }) => {
        if (response?.error) {
          if (GOOGLE_CANCEL_CODES.has(response.error)) {
            settle(() => reject(new OAuthCancelledError(response.error)))
            return
          }
          settle(() => reject(new Error(response.error)))
          return
        }
        const accessToken = response?.access_token
        if (!accessToken) {
          settle(() => reject(new Error("google_no_access_token")))
          return
        }
        try {
          const res = await fetch(
            "https://www.googleapis.com/oauth2/v3/userinfo",
            {
              headers: { authorization: `Bearer ${accessToken}` },
            }
          )
          if (!res.ok) {
            settle(() => reject(new Error("google_userinfo_failed")))
            return
          }
          const info = (await res.json()) as {
            email?: string
            sub?: string
          }
          const sub = info?.sub
          if (!sub) {
            settle(() => reject(new Error("google_userinfo_unparseable")))
            return
          }
          settle(() => resolve({ email: info.email || "", socialId: sub }))
        } catch (fetchError) {
          settle(() =>
            reject(
              fetchError instanceof Error
                ? fetchError
                : new Error("google_userinfo_failed")
            )
          )
        }
      },
      error_callback: (error: { type?: string; message?: string }) => {
        if (settled) return
        const code = error?.type || error?.message || "google_link_failed"
        if (GOOGLE_CANCEL_CODES.has(code)) {
          settle(() => reject(new OAuthCancelledError(code)))
          return
        }
        settle(() => reject(new Error(code)))
      },
    })

    try {
      tokenClient.requestAccessToken({ prompt: "select_account" })
    } catch (requestError) {
      settle(() =>
        reject(
          requestError instanceof Error
            ? requestError
            : new Error("google_link_failed")
        )
      )
    }
  })
}

export async function captureAppleIdentity(
  appleRedirectUri: string
): Promise<OAuthIdentity> {
  if (typeof window === "undefined") {
    throw new Error("oauth_browser_only")
  }
  const AppleID = (window as any).AppleID
  if (!AppleID?.auth) {
    throw new Error("apple_sdk_not_ready")
  }
  AppleID.auth.init({
    clientId: APPLE_OAUTH_CLIENT_ID,
    scope: "name email",
    redirectURI: appleRedirectUri,
    usePopup: true,
  })
  let response: any
  try {
    response = await AppleID.auth.signIn()
  } catch (signInError: any) {
    // Apple SDK rejects with an object like `{error: "popup_closed_by_user"}`
    // when the user dismisses the popup. Map those to a cancellation.
    const code =
      typeof signInError === "string"
        ? signInError
        : signInError?.error || signInError?.message || "apple_link_failed"
    if (APPLE_CANCEL_CODES.has(code)) {
      throw new OAuthCancelledError(code)
    }
    throw new Error(code)
  }
  const idToken: string = response?.authorization?.id_token || ""
  // Apple may include the email in the `user` field on the very first sign-in
  // (and never again). Fall back to that if the id_token doesn't carry one.
  const identityFromToken = idToken ? identityFromIdToken(idToken) : null
  const fallbackEmail: string = response?.user?.email || ""
  if (!identityFromToken) {
    throw new Error("apple_id_token_unparseable")
  }
  return {
    email: identityFromToken.email || fallbackEmail,
    socialId: identityFromToken.socialId,
  }
}
